import { useEffect, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PendingProposalTransaction } from "../hooks/useGovernorProposals";

interface PendingProposalTransactionsProps {
  transactions: PendingProposalTransaction[];
  blockExplorerUrl: string;
  onDismiss: (hash: PendingProposalTransaction["hash"]) => void;
}

const formatElapsedTime = (submittedAt: number, now: number) => {
  const elapsedSeconds = Math.max(0, Math.floor((now - submittedAt) / 1_000));
  if (elapsedSeconds < 60) return `${elapsedSeconds}s`;
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

export const PendingProposalTransactions = ({ transactions, blockExplorerUrl, onDismiss }: PendingProposalTransactionsProps) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <section className="space-y-2" aria-labelledby="pending-proposals-heading">
      <div className="flex items-center gap-2">
        <h4 id="pending-proposals-heading" className="font-medium">Pending Proposals</h4>
        <Badge variant="secondary">{transactions.length}</Badge>
      </div>
      {transactions.length === 0 && (
        <Card>
          <CardContent className="py-5 text-sm text-muted-foreground">
            No pending proposal transactions. Newly submitted proposals will appear here until Polygon confirms them.
          </CardContent>
        </Card>
      )}
      {transactions.map((transaction) => (
        <Card key={transaction.hash} className="border-amber-500/30">
          <CardContent className="flex flex-col gap-3 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-500" />
                <p className="truncate font-medium">{transaction.title}</p>
                <Badge variant="outline">
                  {transaction.stage === "delegation" ? "Activating votes" : "Confirming proposal"}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {transaction.stage === "delegation" ? "Delegation" : "Proposal"} awaiting Polygon confirmation for {formatElapsedTime(transaction.submittedAt, now)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <a
                href={`${blockExplorerUrl}/tx/${transaction.hash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 font-mono text-sm text-primary hover:underline"
              >
                {transaction.hash.slice(0, 10)}...{transaction.hash.slice(-6)}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <Button type="button" size="sm" variant="ghost" onClick={() => onDismiss(transaction.hash)}>
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </section>
  );
};
